import { ref, push, onValue, off, set, serverTimestamp, query, orderByChild, limitToLast } from 'firebase/database';
import { database } from '../config/firebase';

/**
 * Firebase Realtime Chat Service
 * Handles real-time messaging between users
 */

/**
 * Send a message to a chat room
 * @param {string} jobId - Job ID (used as chat room ID)
 * @param {string} senderId - Sender's user ID
 * @param {string} senderName - Sender's name
 * @param {string} message - Message text
 * @returns {Promise<void>}
 */
export const sendMessage = async (jobId, senderId, senderName, message) => {
  try {
    console.log('🔵 Attempting to send message:', { jobId, senderId, senderName, message });
    const messagesRef = ref(database, `chats/${jobId}/messages`);
    const newMessageRef = push(messagesRef);
    
    await set(newMessageRef, {
      senderId,
      senderName,
      message,
      timestamp: Date.now(),
      read: false
    });
    
    console.log('✅ Message sent successfully');
    
    // Update chat metadata
    const chatMetaRef = ref(database, `chats/${jobId}/metadata`);
    await set(chatMetaRef, {
      lastMessage: message,
      lastMessageTime: Date.now(),
      lastSenderId: senderId
    });
    
    console.log('✅ Chat metadata updated');
    
  } catch (error) {
    console.error('❌ Error sending message:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    throw error;
  }
};

/**
 * Listen to messages in a chat room (real-time)
 * @param {string} jobId - Job ID
 * @param {function} callback - Callback function to receive messages
 * @param {number} limit - Number of recent messages to load (default: 50)
 * @returns {function} - Unsubscribe function
 */
export const subscribeToMessages = (jobId, callback, limit = 50) => {
  console.log('🔵 Subscribing to messages for jobId:', jobId);
  const messagesRef = ref(database, `chats/${jobId}/messages`);
  const messagesQuery = query(messagesRef, orderByChild('timestamp'), limitToLast(limit));
  
  const unsubscribe = onValue(messagesQuery, (snapshot) => {
    console.log('📩 Messages snapshot received, count:', snapshot.size);
    const messages = [];
    snapshot.forEach((childSnapshot) => {
      messages.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });
    console.log('✅ Processed messages:', messages.length);
    callback(messages);
  }, (error) => {
    console.error('❌ Error subscribing to messages:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
  });
  
  return () => off(messagesQuery);
};

/**
 * Mark messages as read
 * @param {string} jobId - Job ID
 * @param {string} userId - Current user ID
 */
export const markMessagesAsRead = async (jobId, userId) => {
  try {
    const messagesRef = ref(database, `chats/${jobId}/messages`);
    
    onValue(messagesRef, (snapshot) => {
      snapshot.forEach((childSnapshot) => {
        const message = childSnapshot.val();
        if (message.senderId !== userId && !message.read) {
          const messageRef = ref(database, `chats/${jobId}/messages/${childSnapshot.key}`);
          set(messageRef, { ...message, read: true });
        }
      });
    }, { onlyOnce: true });
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

/**
 * Set typing indicator
 * @param {string} jobId - Job ID
 * @param {string} userId - User ID
 * @param {string} userName - User name
 * @param {boolean} isTyping - Is user typing
 */
export const setTypingIndicator = async (jobId, userId, userName, isTyping) => {
  try {
    const typingRef = ref(database, `chats/${jobId}/typing/${userId}`);
    if (isTyping) {
      await set(typingRef, {
        userName,
        timestamp: Date.now()
      });
    } else {
      await set(typingRef, null);
    }
  } catch (error) {
    console.error('Error setting typing indicator:', error);
  }
};

/**
 * Subscribe to typing indicators
 * @param {string} jobId - Job ID
 * @param {string} currentUserId - Current user ID
 * @param {function} callback - Callback function
 * @returns {function} - Unsubscribe function
 */
export const subscribeToTyping = (jobId, currentUserId, callback) => {
  const typingRef = ref(database, `chats/${jobId}/typing`);
  
  const unsubscribe = onValue(typingRef, (snapshot) => {
    const typingUsers = [];
    snapshot.forEach((childSnapshot) => {
      if (childSnapshot.key !== currentUserId) {
        typingUsers.push(childSnapshot.val());
      }
    });
    callback(typingUsers);
  });
  
  return () => off(typingRef);
};

/**
 * Get unread message count for a chat
 * @param {string} jobId - Job ID
 * @param {string} userId - Current user ID
 * @param {function} callback - Callback with count
 * @returns {function} - Unsubscribe function
 */
export const subscribeToUnreadCount = (jobId, userId, callback) => {
  const messagesRef = ref(database, `chats/${jobId}/messages`);
  
  const unsubscribe = onValue(messagesRef, (snapshot) => {
    let unreadCount = 0;
    snapshot.forEach((childSnapshot) => {
      const message = childSnapshot.val();
      if (message.senderId !== userId && !message.read) {
        unreadCount++;
      }
    });
    callback(unreadCount);
  });
  
  return () => off(messagesRef);
};

/**
 * Initialize chat room with participants
 * @param {string} jobId - Job ID
 * @param {string} clientId - Client user ID
 * @param {string} professionalId - Professional user ID
 */
export const initializeChatRoom = async (jobId, clientId, professionalId) => {
  try {
    const participantsRef = ref(database, `chats/${jobId}/participants`);
    await set(participantsRef, {
      [clientId]: true,
      [professionalId]: true
    });
  } catch (error) {
    console.error('Error initializing chat room:', error);
  }
};

export default {
  sendMessage,
  subscribeToMessages,
  markMessagesAsRead,
  setTypingIndicator,
  subscribeToTyping,
  subscribeToUnreadCount,
  initializeChatRoom
};
